import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import Button from 'components/Button';
import { KEYBOARD_KEYS } from 'constants/common';
import { ICONS_MAP } from 'constants/icons';
import { appReactMemo } from 'hocs';

import {
  Actions,
  Container,
  Header,
  HeaderActions,
  HeaderTitle,
  Subtitle,
  Title,
} from './styled';

export interface IRightSidebar {
  activateDisabled?: boolean;
  buttons?: React.ReactNode;
  children?: React.ReactNode;
  className?: string;
  closeBar: () => void;
  customHeaderButtons?: React.ReactNode;
  customTitleComponent?: React.ReactNode;
  hideActionButtons?: boolean;
  hideSaveButton?: boolean;
  isNew?: boolean;
  isOpen: boolean;
  isSaving?: boolean;
  onActivate?: () => void;
  onCancel?: () => void;
  onSave?: () => void;
  saveButtonText?: string;
  saveButtonType?: 'button' | 'submit';
  saveDisabled?: boolean;
  selectedCount?: number;
  size?: 'lg' | 'md' | 'xl' | 'xxl';
  title?: string;
}

const rightSidebarTestIds = {
  activateButton: {
    container: 'sidebarActivateButton',
  },
  cancelButton: {
    container: 'sidebarCancelButton',
  },
  saveButton: {
    container: 'sidebarSaveButton',
  },
};

const RightSidebar = ({
  activateDisabled,
  buttons,
  children,
  className,
  closeBar,
  customHeaderButtons,
  customTitleComponent,
  hideActionButtons,
  hideSaveButton,
  isNew,
  isOpen,
  isSaving,
  onActivate,
  onCancel,
  onSave,
  saveButtonText,
  saveButtonType = 'submit',
  saveDisabled,
  selectedCount,
  size = 'md',
  title,
}: IRightSidebar) => {
  const { t } = useTranslation();

  const saveButtonRef = useRef<HTMLButtonElement | null>(null);

  const getSaveButtonText = () => {
    if (saveButtonText) return saveButtonText;
    return isNew ? t('common.create') : t('common.save');
  };

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      const { key, target } = e;

      if (key !== KEYBOARD_KEYS.ENTER || !isOpen || hideSaveButton) return;

      const el = target as HTMLElement | null;
      const tag = el?.tagName;

      const isTypingField =
        tag === 'INPUT' ||
        tag === 'TEXTAREA' ||
        tag === 'SELECT' ||
        !!el?.isContentEditable ||
        el?.getAttribute?.('role') === 'textbox';

      if (isTypingField) return;

      e.preventDefault();
      saveButtonRef?.current?.click();
    };

    document.addEventListener('keydown', onKeyDown);

    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isOpen, hideSaveButton]);

  return (
    <Container $isOpen={isOpen} $size={size} className={className}>
      <Header>
        <HeaderTitle>
          <Title size='lg'>{title || t('groupEdit.title')}</Title>
          {!!selectedCount && (
            <Subtitle size='sm'>
              {`${selectedCount}  ${t('groupEdit.selected')}`}
            </Subtitle>
          )}
          {customTitleComponent}
        </HeaderTitle>
        <HeaderActions>
          {customHeaderButtons}
          <Button
            disabled={isSaving}
            iconSize={13}
            onClick={closeBar}
            size='xs'
            startIcon={<ICONS_MAP.Close />}
            type='button'
            variant='icon'
          />
        </HeaderActions>
      </Header>
      {children}
      {hideActionButtons || buttons || (
        <Actions>
          <Button
            disabled={isSaving}
            onClick={onCancel}
            testIds={rightSidebarTestIds.cancelButton}
            type='button'
            variant='outlined'
          >
            {t('common.cancel')}
          </Button>
          {onActivate && (
            <Button
              disabled={activateDisabled}
              onClick={onActivate}
              testIds={rightSidebarTestIds.activateButton}
              type='button'
              variant='outlined'
            >
              {t('common.activate')}
            </Button>
          )}
          {hideSaveButton || (
            <Button
              disabled={isSaving || saveDisabled}
              isLoading={isSaving}
              onClick={onSave}
              ref={saveButtonRef}
              testIds={rightSidebarTestIds.saveButton}
              type={saveButtonType}
              variant='primary'
            >
              {getSaveButtonText()}
            </Button>
          )}
        </Actions>
      )}
    </Container>
  );
};

export default appReactMemo(RightSidebar);
